import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, Severity, Status, type ReportAction, type VehicleReport } from '../api/client'
import { SeverityBars } from '../components/SeverityBars'
import { saveWorkbook } from '../api/openBlob'
import { Tile } from '../components/Tile'
import { cell } from '../components/countMark'
import { EmailReport } from '../components/EmailReport'
import './ReportScreen.css'

/**
 * A compliance report over an explicitly chosen set of vehicles.
 *
 * The ids ARE in the URL here, unlike the register's working selection: this is a
 * document. Its whole value is that it can be re-run, bookmarked, or sent to
 * somebody with "this is what I signed off", and a report you cannot reproduce is
 * an assertion rather than a record.
 *
 * PDF is the browser's own print, not a generated file. window.print() against a
 * print stylesheet gives real selectable text, real page breaks and the user's own
 * paper size, and every browser can already "Save as PDF". A server-side PDF
 * library would be a second layout to maintain that renders slightly differently
 * from the screen — which is how a printed compliance figure ends up disagreeing
 * with the one on screen.
 */
export function ReportScreen() {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const ids = useMemo(
    () =>
      (params.get('ids') ?? '')
        .split(',')
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0),
    [params],
  )

  const [emailing, setEmailing] = useState(false)

  const report = useQuery({
    queryKey: ['report', ids],
    queryFn: () => api.getReport(ids),
    enabled: ids.length > 0,
    // Never served stale: this is the document somebody is about to print, and a
    // cached copy from before an edit would be printed as current.
    staleTime: 0,
  })

  if (ids.length === 0) {
    return (
      <div className="rpt">
        <div className="empty">
          <p>No vehicles were chosen for this report.</p>
          <button type="button" className="btn" onClick={() => navigate('/register')}>
            Back to vehicles
          </button>
        </div>
      </div>
    )
  }

  const r = report.data

  return (
    <div className="rpt">
      {/* Screen only. On paper the reader has the document, not the controls. */}
      <header className="rpt__bar">
        <button type="button" className="btn" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="rpt__actions">
          <button
            type="button"
            className="btn"
            onClick={() =>
              void saveWorkbook(ids, `Prarambh-compliance-${new Date().toISOString().slice(0, 10)}.xlsx`)
            }
          >
            Download Excel
          </button>
          <button
            type="button"
            className={`btn${emailing ? ' btn--primary' : ''}`}
            aria-expanded={emailing}
            onClick={() => setEmailing((v) => !v)}
          >
            Email report
          </button>
          <button type="button" className="btn btn--primary" onClick={() => window.print()}>
            Print / Save as PDF
          </button>
        </div>
      </header>

      {/*
        Screen only, like the bar above it. Emailing sends a server-built PDF and
        records the delivery — which is the difference between this and the Print
        button beside it, where the file never leaves the browser and nothing
        knows it happened.
      */}
      {emailing && ids.length > 0 && (
        <section className="rpt__email">
          <EmailReport
            vehicleIds={ids}
            label={`This report (${ids.length} vehicle${ids.length === 1 ? '' : 's'})`}
            onDone={() => setEmailing(false)}
          />
        </section>
      )}

      {report.isPending && <p className="muted">Building the report…</p>}
      {report.error && <p className="banner banner--error">{(report.error as Error).message}</p>}

      {r && <ReportBody report={r} />}
    </div>
  )
}

function ReportBody({ report }: { report: VehicleReport }) {
  const t = report.totals!

  return (
    <article className="rpt__sheet">
      <header className="rpt__head">
        <div>
          <div className="eyebrow">Prarambh · Fleet Due Date</div>
          <h1>Vehicle compliance report</h1>
        </div>
        <dl className="rpt__meta">
          <dt>As at</dt>
          <dd>{formatDate(report.asOfDate)}</dd>
          <dt>Vehicles</dt>
          <dd>{t.vehicleCount}</dd>
          <dt>Prepared by</dt>
          <dd>{report.generatedBy}</dd>
        </dl>
      </header>

      {/*
        Four numbers, not a chart. These are single headline values, and a hero
        number reads faster than any mark that could carry it.
      */}
      <section className="tiles">
        <Tile label="Vehicles" value={t.vehicleCount} note="in this report" />
        <Tile
          label="Expired obligations"
          value={t.expiredCount}
          note={`across ${t.vehiclesWithExpired} vehicles`}
          tone="crit"
        />
        <Tile label="Due within 90 days" value={t.dueSoonCount} tone="warn" />
        <Tile
          label="Not on record"
          value={t.unknownCount}
          note="nobody has recorded a value"
          tone="unk"
        />
      </section>

      <p className="rpt__note">
        <b>{t.vehiclesClear}</b> of {t.vehicleCount} vehicles need no action. Unknown counts
        as work, not as compliant — nobody can say whether those obligations are met.
        {t.documentCount > 0 && ` ${t.documentCount} certificates are on file.`}
      </p>

      <SeverityBars obligations={report.obligations ?? []} />

      {/* The table view the chart's numbers also live in, so nothing is reachable
          only by reading a bar. */}
      <section className="rpt__block">
        <h2>By obligation</h2>
        <div className="rwrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Obligation</th>
                <th className="num">Expired</th>
                <th className="num">Due soon</th>
                <th className="num">Unknown</th>
                <th className="num">Valid</th>
                <th className="num">No expiry</th>
                <th className="num">N/A</th>
                <th className="num">Certificates</th>
              </tr>
            </thead>
            <tbody>
              {report.obligations?.map((o) => (
                <tr key={o.complianceTypeId}>
                  <td>{o.displayName}</td>
                  <td className="num">{cell(o.expiredCount, 'crit')}</td>
                  <td className="num">{cell(o.dueSoonCount, 'warn')}</td>
                  <td className="num">{cell(o.unknownCount, 'unk')}</td>
                  <td className="num">{o.validCount}</td>
                  <td className="num">{o.noExpiryCount}</td>
                  <td className="num">{o.notApplicableCount}</td>
                  <td className="num">{o.documentCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rpt__block">
        <h2>
          What needs doing
          <span className="rpt__count">{report.actions?.length ?? 0} items</span>
        </h2>

        {report.actions?.length === 0 ? (
          <p className="muted">
            Nothing is overdue, due soon or unrecorded for these vehicles.
          </p>
        ) : (
          <div className="rwrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Site</th>
                  <th>Obligation</th>
                  <th>State</th>
                  <th>Expires</th>
                  <th>Certificate</th>
                </tr>
              </thead>
              <tbody>
                {report.actions?.map((a) => (
                  <tr key={`${a.vehicleId}-${a.complianceTypeId}`}>
                    <td className="mono">{a.registration}</td>
                    <td>{a.site ?? '—'}</td>
                    <td>{a.displayName}</td>
                    <td>
                      <span className={`state state--${tone(a.severity)}`}>
                        {describe(a)}
                      </span>
                    </td>
                    <td className="mono">{a.expiryDate ? formatDate(a.expiryDate) : '—'}</td>
                    <td>{a.documentCount ? 'on file' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rpt__block">
        <h2>Vehicles</h2>
        <div className="rwrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Registration</th>
                <th>Type</th>
                <th>Make / model</th>
                <th>Site</th>
                <th className="num">Expired</th>
                <th className="num">Due soon</th>
                <th className="num">Unknown</th>
                <th className="num">Certs</th>
              </tr>
            </thead>
            <tbody>
              {report.vehicles?.map((v) => (
                <tr key={v.vehicleId}>
                  <td className="mono">{v.registration}</td>
                  <td>{v.vehicleType ?? '—'}</td>
                  <td>{[v.make, v.model].filter(Boolean).join(' ') || '—'}</td>
                  <td>{v.site ?? 'unassigned'}</td>
                  <td className="num">{cell(v.expiredCount, 'crit')}</td>
                  <td className="num">{cell(v.dueSoonCount, 'warn')}</td>
                  <td className="num">{cell(v.unknownCount, 'unk')}</td>
                  <td className="num">{v.documentCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <footer className="rpt__foot">
        Generated {formatDateTime(report.generatedAtUtc)} by {report.generatedBy}. Dates are as
        recorded in Prarambh at that moment; the register is corrected as certificates arrive,
        so re-run this report rather than relying on an old copy.
      </footer>
    </article>
  )
}

function tone(severity: number | undefined): string {
  if (severity === Severity.Expired) return 'crit'
  if (severity === Severity.DueSoon) return 'warn'
  return 'unk'
}

function describe(a: ReportAction): string {
  if (a.status === Status.Unknown) return 'Not on record'
  if (a.severity === Severity.Expired) {
    return a.daysUntilExpiry != null ? `Expired ${age(-a.daysUntilExpiry)} ago` : 'Expired'
  }
  return a.daysUntilExpiry != null ? `${age(a.daysUntilExpiry)} left` : 'Due soon'
}

function age(days: number): string {
  if (days >= 730) return `${Math.round(days / 365)}y`
  if (days >= 60) return `${Math.round(days / 30)}mo`
  return `${days}d`
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
